import { DocumentNode } from '@apollo/client';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { useCallback, useEffect, useMemo } from 'react';

import { InviteConfirmationModalTest, useMultipleVariantData } from '@pflegia/feature-flags';
import { ApplicationSource } from '@pflegia/types/common/enums/application-source.enum';
import { CardStatus, CardType } from '@pflegia/types/common/job-position/card';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { collectUtmData } from '@pflegia/ui/application/marketing/utm/utils/collect-utm-data';
import { removeUtmKeysFromCookies } from '@pflegia/ui/application/marketing/utm/utils/remove-utm-keys-from-cookies';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useConsentBanner } from '@pflegia/ui/contexts/consent-banner.context';
import { ButtonVariant } from '@pflegia/ui/shared/button';
import BaseOffersDialogueButton from '@pflegia/ui/shared/position-card-buttons/OffersDialogueButton';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';

import { useNavigationHelper } from '@hooks/use-navigation-helper';
import { useRouterHelper } from '@hooks/use-router-helper';

import { CHAT_BY_JOB_POSITION_ID } from '@shared/application/constants';
import { useJobDetails } from '@shared/context/job-details.context';

import { clearAutoAcceptInvite, shouldAutoAcceptInvite } from '@utils/auto-accept-invite';
import { getInviteSource } from '@utils/get-invite-source';

import SignUpButton from '../sign-up-dialogue/SignUpButton.component';
import { useDialogButtonActions } from './hooks/use-dialog-button-actions';
import { useSuggestionModalLogic } from './hooks/use-suggestion-modal-logic';

export interface OffersDialogueButtonProps {
  status?: CardStatus;
  type?: CardType;
  label?: string;
  className?: string;
  jobPositionId: string;
  jobTitle: string;
  slug?: string | null;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  employerName?: string;
  colorScheme?: ButtonVariant;
  slimmed?: boolean;
  refetchQueries?: DocumentNode[];
  handleAfterAccept?: () => void;
}

const OffersDialogueButton: React.FC<OffersDialogueButtonProps> = ({
  className,
  label,
  status = 'pending',
  type = 'default',
  jobPositionId,
  jobTitle,
  slug,
  openDialog,
  employerName,
  colorScheme = 'apply',
  slimmed = false,
  refetchQueries,
  handleAfterAccept,
}) => {
  const {
    store: { open: suggestionsModalVisible },
  } = useSuggestionModalLogic();
  const { jobPositionId: storeJobPositionId, set, requestConfirmation } = useMatchDialogueStore();
  const { applyJobPosition, acceptJobPosition, loading } = useDialogButtonActions(jobPositionId);
  const { payload: inviteConfirmationModalTest } = useMultipleVariantData(InviteConfirmationModalTest.Name);
  const { isOffersPage, isJobPage } = useRouterHelper();
  const jobDetails = useJobDetails();
  const authenticated = useAuth();
  const { isConsentResolved } = useConsentBanner();
  const { t } = useTranslation('job', { keyPrefix: 'signUpDialogue' });

  const navigationHelper = useNavigationHelper();

  const applicationSource = navigationHelper.applicationSource ?? ApplicationSource.Registration;

  const chatUrl = CHAT_BY_JOB_POSITION_ID(jobPositionId);

  const matchDialogInfo = useMemo(
    () => ({ status, type, jobPositionId, jobPositionTitle: jobTitle, employerName }),
    [status, type, jobPositionId, jobTitle, employerName],
  );

  const ensureJobPositionIdSync = useCallback(() => {
    if (storeJobPositionId !== jobPositionId) {
      set('jobPositionId', jobPositionId);
    }
  }, [storeJobPositionId, jobPositionId, set]);

  const handleDialogOpen = useCallback(
    (matchId?: string) => {
      if (suggestionsModalVisible) {
        openDialog(matchDialogInfo);
        return;
      }
      ensureJobPositionIdSync();
      openDialog({ ...matchDialogInfo, matchId });
    },
    [suggestionsModalVisible, matchDialogInfo, openDialog, ensureJobPositionIdSync],
  );

  const onApply = useCallback(async () => {
    const utm = collectUtmData({ withInitial: true });
    await applyJobPosition({
      variables: { jobPositionId, applicationSource, slimmedJobCard: slimmed, utm },
      ...(refetchQueries?.length ? { refetchQueries } : {}),
      onCompleted: async data => {
        removeUtmKeysFromCookies();
        await jobDetails?.onMatchUpdate?.(data.match);
        handleDialogOpen(data.match.id);
      },
    });
  }, [applyJobPosition, jobPositionId, applicationSource, slimmed, refetchQueries, jobDetails, handleDialogOpen]);

  const onAccept = useCallback(async () => {
    const inviteAcceptSource = getInviteSource(jobPositionId);
    await acceptJobPosition({
      variables: { inviteAcceptSource, jobPositionId },
      ...(refetchQueries?.length ? { refetchQueries } : {}),
      onCompleted: data => {
        handleAfterAccept?.();
        handleDialogOpen(data.acceptOffer.id);
      },
    });
  }, [acceptJobPosition, jobPositionId, refetchQueries, handleAfterAccept, handleDialogOpen]);

  useEffect(() => {
    if (!authenticated || !isConsentResolved || status !== 'pending' || type !== 'invitation') return;
    if (!shouldAutoAcceptInvite(jobPositionId)) return;

    clearAutoAcceptInvite();
    onAccept();
  }, [authenticated, isConsentResolved, status, type, jobPositionId, onAccept]);

  const openConfirmationDialog = useCallback(
    () =>
      new Promise<boolean>(resolve =>
        requestConfirmation({ ...matchDialogInfo, state: 'confirmInvitation' }, (accepted: boolean) => resolve(accepted)),
      ),
    [matchDialogInfo, requestConfirmation],
  );

  const onApplyAndOnAccept = useCallback(async () => {
    if (suggestionsModalVisible) {
      openDialog(matchDialogInfo);
      return;
    }

    ensureJobPositionIdSync();

    if (status === 'pending' && type === 'default') {
      await onApply();
    } else if (status === 'pending' && type === 'invitation') {
      if ((isOffersPage || isJobPage) && inviteConfirmationModalTest) {
        const confirmed = await openConfirmationDialog();
        if (!confirmed) return;
      }
      await onAccept();
    } else {
      openDialog(matchDialogInfo);
    }
  }, [
    suggestionsModalVisible,
    ensureJobPositionIdSync,
    status,
    type,
    openDialog,
    matchDialogInfo,
    onApply,
    isOffersPage,
    isJobPage,
    inviteConfirmationModalTest,
    onAccept,
    openConfirmationDialog,
  ]);

  return authenticated ? (
    <BaseOffersDialogueButton
      className={className}
      label={label}
      status={status}
      type={type}
      jobPositionId={jobPositionId}
      jobTitle={jobTitle}
      openDialog={openDialog as (connectionInfo: unknown) => void}
      employerName={employerName}
      colorScheme={colorScheme}
      loading={loading}
      chatUrl={chatUrl}
      jobPositionAttributes={{ id: jobPositionId, jobTitle, slug }}
      onApplyAndOnAccept={onApplyAndOnAccept}
    />
  ) : (
    <SignUpButton
      jobPositionAttributes={{ id: jobPositionId, jobTitle, slug }}
      colorScheme='apply'
      className={clsx('w-full sm:w-auto', className)}
      label={label ?? t('apply')}
    />
  );
};

export default OffersDialogueButton;
